import { HierarchyLevel, SalesHierarchyNode, TableRow, ForecastOpportunityMetadata, SalesDataPoint } from "../types"
import { MONTHS_PER_WEEK } from "./periods"
import { plan_quantity_basis, type QuantityBasis } from "./quantity_basis"
import { round_cases } from "./quantity_basis"

export type OpportunityScenarioView = "baseline" | "weighted" | "full_upside"

export interface ForecastOpportunitySummary extends ForecastOpportunityMetadata {
  weighted_revenue: number
  full_revenue: number
  row_count: number
  /** Deepest hierarchy trail where this opportunity appears on a leaf (for cards). */
  hierarchy_path?: string
}

export interface OpportunityPrefillGrowthExpectation {
  id: string
  name: string
  initialDoors: number
  startMonth: string
  storeCountGrowthMultiplier: number
  productLineName: string
  initialCasesPerMonth: number
  costPerCase: number
  sameStoreSalesMultiplier: number
  productSku?: string
  skuName?: string
  // Catalog identity carried from the source node (stable_key / metadata), so a
  // prefill from an already-mapped row never claims it can't receive actuals.
  productId?: string
  productUpc?: string
  unitsPerCase?: number
}

export interface OpportunityPrefillFormData {
  classOfTrade: string
  customer: string
  routeToMarket: string
  customerIsOther: boolean
  planningBySku: boolean | null
  isOpportunity: boolean
  opportunityName: string
  opportunityLikelihood: number
  growthExpectations: OpportunityPrefillGrowthExpectation[]
}

const OPPORTUNITY_COLORS = ["#0ea5e9", "#f59e0b", "#10b981", "#ec4899", "#8b5cf6", "#14b8a6", "#f43f5e"]

export function getOpportunityColor(index: number): string {
  return OPPORTUNITY_COLORS[index % OPPORTUNITY_COLORS.length]
}

export function isOpportunityNode(node: { metadata?: { opportunity?: ForecastOpportunityMetadata } | null }): boolean {
  return Boolean(node.metadata?.opportunity && node.metadata.opportunity.status !== "live")
}

export function getOpportunity(node: { metadata?: { opportunity?: ForecastOpportunityMetadata } | null }): ForecastOpportunityMetadata | null {
  return node.metadata?.opportunity || null
}

/**
 * Drop every non-live opportunity branch from the tree. Used when the Opportunities panel is
 * closed: the plan shows only what's committed, and deals appear (at their scenario weight)
 * once the toggle is on. Promoted (`live`) branches are part of the plan and always stay.
 *
 * A node whose children ALL pruned away goes too — an opportunity-only customer (a Costco
 * region that exists purely as a deal) would otherwise survive as a childless node whose
 * load-time aggregated `sales_data` still carries the weighted deal volume, and leaf-walking
 * totals would count it. Callers must re-aggregate parents afterwards for the same reason.
 */
export function pruneOpenOpportunityBranches(nodes: SalesHierarchyNode[]): SalesHierarchyNode[] {
  function pruneNode(node: any): any | null {
    if (isOpportunityNode(node)) {
      return null
    }
    if (!Array.isArray(node.children) || node.children.length === 0) {
      return node
    }
    const children = node.children
      .map((child: any) => pruneNode(child))
      .filter((child: any) => child != null)
    if (children.length === 0) {
      return null
    }
    return children.length === node.children.length && children.every((child: any, index: number) => child === node.children[index])
      ? node
      : { ...node, children }
  }
  return nodes.map((node: any) => pruneNode(node)).filter((node: any) => node != null)
}

/** Ancestor node ids from root through each node whose open opportunity id matches (table expand state). */
export function collectExpandedAncestorIdsForOpportunityId(
  roots: SalesHierarchyNode[],
  opportunityId: string
): Set<string> {
  const ids = new Set<string>()
  function walk(node: SalesHierarchyNode, chain: string[]): void {
    const nextChain = [...chain, node.id]
    const op = node.metadata?.opportunity
    if (op && op.status !== "live" && op.id === opportunityId) {
      nextChain.forEach((id: string) => ids.add(id))
    }
    if (Array.isArray(node.children)) {
      for (const ch of node.children as SalesHierarchyNode[]) {
        walk(ch, nextChain)
      }
    }
  }
  roots.forEach((r: SalesHierarchyNode) => walk(r, []))
  return ids
}

/** Expand every row on the path to any open (non-live) opportunity row. */
export function collectExpandedAncestorIdsForAllOpenOpportunities(roots: SalesHierarchyNode[]): Set<string> {
  const ids = new Set<string>()
  function walk(node: SalesHierarchyNode, chain: string[]): void {
    const nextChain = [...chain, node.id]
    const op = node.metadata?.opportunity
    if (op && op.status !== "live") {
      nextChain.forEach((id: string) => ids.add(id))
    }
    if (Array.isArray(node.children)) {
      for (const ch of node.children as SalesHierarchyNode[]) {
        walk(ch, nextChain)
      }
    }
  }
  roots.forEach((r: SalesHierarchyNode) => walk(r, []))
  return ids
}

function normalizeHierarchyName(name: string): string {
  return name.toLowerCase().trim()
}

function isCustomerLikeLevel(level: string): boolean {
  return (
    level === HierarchyLevel.CUSTOMER ||
    level === HierarchyLevel.RETAILER ||
    String(level) === "customer"
  )
}

/** Rows that represent a buyer / account name we can match to an opportunity "customer". */
function isBuyerAnchorLevel(level: string): boolean {
  return (
    isCustomerLikeLevel(level) ||
    level === HierarchyLevel.DISTRIBUTOR ||
    String(level) === "distributor"
  )
}

function isClassOfTradeLevel(level: string): boolean {
  return level === HierarchyLevel.CLASS_OF_TRADE || String(level) === "class_of_trade"
}

function forecastRowVolumeDollars(node: SalesHierarchyNode): number {
  if (!Array.isArray(node.sales_data)) return 0
  return node.sales_data.reduce(
    (sum: number, dp: SalesDataPoint) => sum + Number(dp.metrics?.volume_dollars || 0),
    0
  )
}

/** When multiple hierarchy nodes share the same buyer name, prefer the one that already carries the plan (highest $). */
function pickBestBaseBuyer(candidates: SalesHierarchyNode[]): SalesHierarchyNode | null {
  if (!candidates.length) return null
  return candidates.reduce((best: SalesHierarchyNode, node: SalesHierarchyNode) =>
    forecastRowVolumeDollars(node) > forecastRowVolumeDollars(best) ? node : best
  )
}

function findOpportunityBuyerChild(root: SalesHierarchyNode): SalesHierarchyNode | null {
  if (!Array.isArray(root.children)) return null
  for (const child of root.children) {
    const n = child as SalesHierarchyNode
    if (isBuyerAnchorLevel(String(n.level)) && isOpportunityNode(n)) {
      return n
    }
  }
  return null
}

/** Add source period metrics into target (used for meshing / splicing). */
export function mergeHierarchySalesData(targetNode: SalesHierarchyNode, sourceNode: SalesHierarchyNode): void {
  if (!Array.isArray(targetNode.sales_data)) {
    targetNode.sales_data = []
  }
  if (!Array.isArray(sourceNode.sales_data) || sourceNode.sales_data.length === 0) {
    return
  }
  const targetByPeriod = new Map<string, SalesDataPoint>()
  targetNode.sales_data.forEach((dataPoint: SalesDataPoint) => targetByPeriod.set(dataPoint.period.period_key, dataPoint))

  sourceNode.sales_data.forEach((dataPoint: SalesDataPoint) => {
    const existing = targetByPeriod.get(dataPoint.period.period_key)
    if (!existing) {
      targetNode.sales_data.push(structuredClone(dataPoint))
      return
    }
    existing.metrics = {
      ...existing.metrics,
      store_count: Number(existing.metrics.store_count || 0) + Number(dataPoint.metrics.store_count || 0),
      cases_sold: Number(existing.metrics.cases_sold || 0) + Number(dataPoint.metrics.cases_sold || 0),
      volume_dollars: Number(existing.metrics.volume_dollars || 0) + Number(dataPoint.metrics.volume_dollars || 0),
      case_cost: Number(existing.metrics.case_cost || dataPoint.metrics.case_cost || 0),
    }
  })
}

/**
 * Opportunity trees often use a different class-of-trade path than existing rows, so path-based meshing misses.
 * When an open opportunity is Class of Trade → Customer (single) and a unique non-opportunity customer exists
 * with the same name, splice product lines (and nested SKUs) under that customer and drop the duplicate branch.
 * Opportunity metadata stays on merged/appended product lines so deleting an opportunity does not remove the customer.
 */
export function spliceOpportunityBranchesIntoExistingCustomers(nodes: SalesHierarchyNode[]): SalesHierarchyNode[] {
  const clonedForest = nodes.map((node: SalesHierarchyNode) => structuredClone(node))
  const baseCustomersByName = new Map<string, SalesHierarchyNode[]>()

  function walkCollectBase(node: SalesHierarchyNode): void {
    if (isBuyerAnchorLevel(String(node.level)) && !isOpportunityNode(node)) {
      const key = normalizeHierarchyName(node.name)
      const list = baseCustomersByName.get(key) ?? []
      list.push(node)
      baseCustomersByName.set(key, list)
    }
    if (Array.isArray(node.children)) {
      node.children.forEach((child: any) => walkCollectBase(child as SalesHierarchyNode))
    }
  }

  clonedForest.forEach(walkCollectBase)

  function mergeOrAppendOpportunityChild(baseParent: SalesHierarchyNode, oppChild: SalesHierarchyNode): void {
    if (!baseParent.children) baseParent.children = []
    const childName = normalizeHierarchyName(oppChild.name)
    const levelStr = String(oppChild.level)
    const existing = baseParent.children.find(
      (c: any) => String(c.level) === levelStr && normalizeHierarchyName(String(c.name)) === childName
    ) as SalesHierarchyNode | undefined

    if (existing) {
      const opp_incoming = getOpportunity(oppChild)
      const opp_existing = getOpportunity(existing)
      const separate_opportunity_branch =
        opp_incoming && (!opp_existing || opp_existing.id !== opp_incoming.id)
      if (separate_opportunity_branch) {
        const moved = structuredClone(oppChild) as SalesHierarchyNode
        moved.parent_id = baseParent.id
        const tag = (opp_incoming.name && opp_incoming.name.trim()) || "Opportunity"
        moved.name = `${String(oppChild.name)} — ${tag}`
        moved.id = `${oppChild.id}-sep-${opp_incoming.id}`
        baseParent.children.push(moved as any)
        return
      }

      mergeHierarchySalesData(existing, oppChild)
      const oppMeta = getOpportunity(oppChild)
      if (oppMeta) {
        existing.metadata = { ...(existing.metadata || {}), opportunity: oppMeta }
      }
      if (Array.isArray(oppChild.children)) {
        oppChild.children.forEach((grandchild: any) =>
          mergeOrAppendOpportunityChild(existing, grandchild as SalesHierarchyNode)
        )
      }
    } else {
      const moved = structuredClone(oppChild) as SalesHierarchyNode
      moved.parent_id = baseParent.id
      baseParent.children.push(moved as any)
    }
  }

  function spliceOpportunityBuyerIntoBase(buyerNode: SalesHierarchyNode, baseBuyer: SalesHierarchyNode): void {
    mergeHierarchySalesData(baseBuyer, buyerNode)
    if (Array.isArray(buyerNode.children)) {
      buyerNode.children.forEach((ch: any) =>
        mergeOrAppendOpportunityChild(baseBuyer, ch as SalesHierarchyNode)
      )
    }
  }

  function trySpliceOpportunityRoot(root: SalesHierarchyNode): SalesHierarchyNode | null {
    if (!isClassOfTradeLevel(String(root.level)) || !isOpportunityNode(root) || !Array.isArray(root.children)) {
      return root
    }
    while (true) {
      const customerNode = findOpportunityBuyerChild(root)
      if (!customerNode) {
        break
      }
      const key = normalizeHierarchyName(customerNode.name)
      const candidates = baseCustomersByName.get(key)
      const baseCustomer = pickBestBaseBuyer(candidates ?? [])
      if (!baseCustomer || baseCustomer.id === customerNode.id) {
        break
      }
      spliceOpportunityBuyerIntoBase(customerNode, baseCustomer)
      root.children = root.children.filter((ch: any) => (ch as SalesHierarchyNode).id !== customerNode.id)
    }
    if (!root.children.length) {
      return null
    }
    return root
  }

  /** Backend or older saves can leave an opportunity buyer as its own root (broken parent links). */
  function trySpliceFloatingOpportunityBuyerRoot(root: SalesHierarchyNode): SalesHierarchyNode | null {
    if (isClassOfTradeLevel(String(root.level))) {
      return root
    }
    if (!isBuyerAnchorLevel(String(root.level)) || !isOpportunityNode(root)) {
      return root
    }
    const key = normalizeHierarchyName(root.name)
    const candidates = baseCustomersByName.get(key)
    const baseBuyer = pickBestBaseBuyer(candidates ?? [])
    if (!baseBuyer || baseBuyer.id === root.id) {
      return root
    }
    spliceOpportunityBuyerIntoBase(root, baseBuyer)
    return null
  }

  const nextRoots: SalesHierarchyNode[] = []
  for (const root of clonedForest) {
    let result = trySpliceOpportunityRoot(root)
    if (result !== null) {
      result = trySpliceFloatingOpportunityBuyerRoot(result)
    }
    if (result !== null) {
      nextRoots.push(result)
    }
  }
  return nextRoots
}

function hierarchy_level_caption(level: string): string {
  const l = String(level)
  if (l === HierarchyLevel.CLASS_OF_TRADE || l === "class_of_trade") return "Class of trade"
  if (l === HierarchyLevel.DISTRIBUTOR || l === "distributor") return "Distributor"
  if (l === HierarchyLevel.DISTRIBUTION_CENTER || l === "distribution_center") return "Distribution center"
  if (l === HierarchyLevel.CUSTOMER || l === HierarchyLevel.RETAILER || l === "customer") return "Customer"
  if (l === HierarchyLevel.PRODUCT_LINE || l === "product_line") return "Product line"
  if (l === HierarchyLevel.SKU || l === "sku") return "SKU"
  return l.replace(/_/g, " ")
}

function format_hierarchy_path(trail: { name: string; level: string }[]): string {
  return trail
    .map((t: { name: string; level: string }) => `${t.name} (${hierarchy_level_caption(t.level)})`)
    .join(" → ")
}

export function collectForecastOpportunities(nodes: SalesHierarchyNode[]): ForecastOpportunitySummary[] {
  const opportunities = new Map<string, ForecastOpportunitySummary & { _path_depth?: number }>()

  function visit(node: any, trail: { name: string; level: string }[]): void {
    const nextTrail = [...trail, { name: String(node.name ?? ""), level: String(node.level ?? "") }]
    const opportunity = getOpportunity(node)
    // Only OPEN deals belong in the pipeline panel and its weighted totals; won deals are
    // baseline now and lost deals are history (see `collectClosedForecastOpportunities`).
    if (opportunity && opportunity.status !== "live" && opportunity.status !== "lost") {
      const current = opportunities.get(opportunity.id) || {
        ...opportunity,
        weighted_revenue: 0,
        full_revenue: 0,
        row_count: 0,
      }

      // Keep summary metadata aligned with any node carrying this opportunity (likelihood edits update every node).
      current.likelihood_pct = opportunity.likelihood_pct
      current.name = opportunity.name
      current.color = opportunity.color

      if (!Array.isArray(node.children) || node.children.length === 0) {
        current.row_count += 1
        const pct = Math.max(0, Math.min(100, Number(opportunity.likelihood_pct ?? 100))) / 100
        for (const dataPoint of node.sales_data || []) {
          const base = dataPoint.opportunity_base_metrics
          const fullVol = Number(base?.volume_dollars ?? dataPoint.metrics?.volume_dollars ?? 0)
          current.full_revenue += fullVol
          // Prefer explicit full × likelihood when base metrics exist (stable when likelihood slider changes).
          current.weighted_revenue +=
            base != null ? fullVol * pct : Number(dataPoint.metrics?.volume_dollars ?? 0)
        }
        const pathStr = format_hierarchy_path(nextTrail)
        const depth = nextTrail.length
        if (!current._path_depth || depth > current._path_depth) {
          current._path_depth = depth
          current.hierarchy_path = pathStr
        }
      }

      opportunities.set(opportunity.id, current)
    }

    if (Array.isArray(node.children)) {
      node.children.forEach((child: any) => visit(child, nextTrail))
    }
  }

  nodes.forEach((node: SalesHierarchyNode) => visit(node, []))
  return Array.from(opportunities.values()).map((o: ForecastOpportunitySummary & { _path_depth?: number }) => {
    const { _path_depth: _d, ...rest } = o
    return rest
  })
}

export function applyOpportunityScenario(
  nodes: SalesHierarchyNode[],
  scenarioView: OpportunityScenarioView,
  selectedOpportunityIds: Set<string>
): SalesHierarchyNode[] {
  function transformNode(node: any): any {
    const opportunity = getOpportunity(node)
    const hiddenBySelection = opportunity && selectedOpportunityIds.size > 0 && !selectedOpportunityIds.has(opportunity.id)
    const shouldTransform = Boolean(opportunity && opportunity.status !== "live")
    // A lost deal contributes nothing in every scenario — its rows are history, not upside.
    const multiplier = shouldTransform
      ? opportunity!.status === "lost" || hiddenBySelection || scenarioView === "baseline"
        ? 0
        : scenarioView === "full_upside"
          ? 1
          : Math.max(0, Math.min(100, opportunity!.likelihood_pct)) / 100
      : 1

    const salesData = Array.isArray(node.sales_data)
      ? node.sales_data.map((dataPoint: any) => {
        if (!shouldTransform) return dataPoint
        const baseMetrics = dataPoint.opportunity_base_metrics || dataPoint.metrics
        return {
          ...dataPoint,
          metrics: {
            ...dataPoint.metrics,
            store_count: Math.round(Number(baseMetrics.store_count || 0) * multiplier),
            cases_sold: round_cases(Number(baseMetrics.cases_sold || 0) * multiplier),
            volume_dollars: Number(baseMetrics.volume_dollars || 0) * multiplier,
            case_cost: Number(baseMetrics.case_cost || dataPoint.metrics?.case_cost || 0),
          },
          opportunity_base_metrics: baseMetrics,
        }
      })
      : []

    return {
      ...node,
      sales_data: salesData,
      children: Array.isArray(node.children) ? node.children.map((child: any) => transformNode(child)) : node.children,
    }
  }

  return nodes.map((node: SalesHierarchyNode) => transformNode(node))
}

export function updateOpportunityLikelihood(
  nodes: SalesHierarchyNode[],
  opportunityId: string,
  likelihoodPct: number
): SalesHierarchyNode[] {
  const safeLikelihood = Math.max(0, Math.min(100, likelihoodPct))

  function transformNode(node: any): any {
    const opportunity = getOpportunity(node)
    const isTarget = opportunity?.id === opportunityId
    const multiplier = safeLikelihood / 100
    const salesData = Array.isArray(node.sales_data)
      ? node.sales_data.map((dataPoint: any) => {
        if (!isTarget) return dataPoint
        const baseMetrics = dataPoint.opportunity_base_metrics || dataPoint.metrics
        return {
          ...dataPoint,
          metrics: {
            ...dataPoint.metrics,
            store_count: Math.round(Number(baseMetrics.store_count || 0) * multiplier),
            cases_sold: round_cases(Number(baseMetrics.cases_sold || 0) * multiplier),
            volume_dollars: Number(baseMetrics.volume_dollars || 0) * multiplier,
            case_cost: Number(baseMetrics.case_cost || dataPoint.metrics?.case_cost || 0),
          },
          opportunity_base_metrics: baseMetrics,
        }
      })
      : []
    const metadata = isTarget
      ? { ...(node.metadata || {}), opportunity: { ...opportunity, likelihood_pct: safeLikelihood } }
      : node.metadata

    return {
      ...node,
      metadata,
      sales_data: salesData,
      children: Array.isArray(node.children) ? node.children.map((child: any) => transformNode(child)) : node.children,
    }
  }

  return nodes.map((node: SalesHierarchyNode) => transformNode(node))
}

export function promoteOpportunityToLive(nodes: SalesHierarchyNode[], opportunityId: string): SalesHierarchyNode[] {
  function transformNode(node: any): any {
    const opportunity = getOpportunity(node)
    const isTarget = opportunity?.id === opportunityId
    const salesData = Array.isArray(node.sales_data)
      ? node.sales_data.map((dataPoint: any) => {
        if (!isTarget) return dataPoint
        const baseMetrics = dataPoint.opportunity_base_metrics || dataPoint.metrics
        return {
          ...dataPoint,
          metrics: baseMetrics,
          source_kind: "forecast",
          is_forecast: true,
          manual_note: undefined,
          // Keep the unscaled record: winning used to destroy it, which made a promotion
          // unauditable ("this $1M used to be a 40% deal") and impossible to reopen.
          opportunity_base_metrics: baseMetrics,
        }
      })
      : []
    const nextMetadata = isTarget
      ? {
          ...(node.metadata || {}),
          opportunity: {
            ...opportunity,
            status: "live",
            likelihood_pct: 100,
            likelihood_pct_at_close: opportunity!.likelihood_pct_at_close ?? opportunity!.likelihood_pct,
          },
        }
      : node.metadata

    return {
      ...node,
      metadata: nextMetadata,
      sales_data: salesData,
      children: Array.isArray(node.children) ? node.children.map((child: any) => transformNode(child)) : node.children,
    }
  }

  return nodes.map((node: SalesHierarchyNode) => transformNode(node))
}

/**
 * Mark an open opportunity lost: the branch stays in the tree (pipeline history and
 * win-rate reporting need it) with its unscaled record intact, but its metrics zero out
 * so it contributes nothing to any total, scenario, or saved version.
 */
export function markOpportunityLost(nodes: SalesHierarchyNode[], opportunityId: string): SalesHierarchyNode[] {
  function transformNode(node: any): any {
    const opportunity = getOpportunity(node)
    const isTarget = opportunity?.id === opportunityId
    const salesData = Array.isArray(node.sales_data)
      ? node.sales_data.map((dataPoint: any) => {
        if (!isTarget) return dataPoint
        const baseMetrics = dataPoint.opportunity_base_metrics || dataPoint.metrics
        return {
          ...dataPoint,
          metrics: {
            ...dataPoint.metrics,
            store_count: 0,
            cases_sold: 0,
            volume_dollars: 0,
            ...(dataPoint.metrics?.units_sold != null ? { units_sold: 0 } : {}),
          },
          opportunity_base_metrics: baseMetrics,
        }
      })
      : []
    const nextMetadata = isTarget
      ? {
          ...(node.metadata || {}),
          opportunity: {
            ...opportunity,
            status: "lost",
            likelihood_pct_at_close: opportunity!.likelihood_pct_at_close ?? opportunity!.likelihood_pct,
          },
        }
      : node.metadata

    return {
      ...node,
      metadata: nextMetadata,
      sales_data: salesData,
      children: Array.isArray(node.children) ? node.children.map((child: any) => transformNode(child)) : node.children,
    }
  }

  return nodes.map((node: SalesHierarchyNode) => transformNode(node))
}

/**
 * Reopen a won or lost opportunity: restore `open` status, the likelihood it closed at,
 * and the likelihood-weighted metrics recomputed from the preserved base record.
 */
export function reopenOpportunity(nodes: SalesHierarchyNode[], opportunityId: string): SalesHierarchyNode[] {
  function transformNode(node: any): any {
    const opportunity = getOpportunity(node)
    const isTarget = opportunity?.id === opportunityId && opportunity.status !== "open"
    const restored_pct = isTarget
      ? Math.max(0, Math.min(100, Number(opportunity!.likelihood_pct_at_close ?? opportunity!.likelihood_pct ?? 100)))
      : 100
    const multiplier = restored_pct / 100
    const salesData = Array.isArray(node.sales_data)
      ? node.sales_data.map((dataPoint: any) => {
        if (!isTarget) return dataPoint
        const baseMetrics = dataPoint.opportunity_base_metrics || dataPoint.metrics
        return {
          ...dataPoint,
          metrics: {
            ...dataPoint.metrics,
            store_count: Math.round(Number(baseMetrics.store_count || 0) * multiplier),
            cases_sold: round_cases(Number(baseMetrics.cases_sold || 0) * multiplier),
            volume_dollars: Number(baseMetrics.volume_dollars || 0) * multiplier,
          },
          source_kind: "opportunity",
          opportunity_base_metrics: baseMetrics,
        }
      })
      : []
    const nextMetadata = isTarget
      ? {
          ...(node.metadata || {}),
          opportunity: { ...opportunity, status: "open", likelihood_pct: restored_pct },
        }
      : node.metadata

    return {
      ...node,
      metadata: nextMetadata,
      sales_data: salesData,
      children: Array.isArray(node.children) ? node.children.map((child: any) => transformNode(child)) : node.children,
    }
  }

  return nodes.map((node: SalesHierarchyNode) => transformNode(node))
}

/** Won and lost opportunities, for the panel's Closed section (reopen / win-rate review). */
export function collectClosedForecastOpportunities(nodes: SalesHierarchyNode[]): ForecastOpportunitySummary[] {
  const closed = new Map<string, ForecastOpportunitySummary>()

  function visit(node: any): void {
    const opportunity = getOpportunity(node)
    if (opportunity && (opportunity.status === "live" || opportunity.status === "lost")) {
      const current = closed.get(opportunity.id) || {
        ...opportunity,
        weighted_revenue: 0,
        full_revenue: 0,
        row_count: 0,
      }
      current.status = opportunity.status
      if (!Array.isArray(node.children) || node.children.length === 0) {
        current.row_count += 1
        for (const dataPoint of node.sales_data || []) {
          const base = dataPoint.opportunity_base_metrics
          current.full_revenue += Number(base?.volume_dollars ?? dataPoint.metrics?.volume_dollars ?? 0)
        }
      }
      closed.set(opportunity.id, current)
    }
    for (const child of node.children ?? []) visit(child)
  }

  nodes.forEach((node: SalesHierarchyNode) => visit(node))
  return [...closed.values()]
}

/**
 * After promoting an opportunity to live, remove stale placeholder rows such as
 * "Product X — <opportunity name>" when they carry no measurable data and no subtree data.
 */
export function cleanupPromotedOpportunityPlaceholders(nodes: SalesHierarchyNode[]): SalesHierarchyNode[] {
  function subtreeHasData(node: SalesHierarchyNode): boolean {
    const own_data = (node.sales_data || []).some((dp: SalesDataPoint) => {
      const m = dp.metrics || ({} as any)
      return Number(m.store_count || 0) !== 0 || Number(m.cases_sold || 0) !== 0 || Number(m.volume_dollars || 0) !== 0
    })
    if (own_data) return true
    if (!Array.isArray(node.children)) return false
    return node.children.some((child: any) => subtreeHasData(child as SalesHierarchyNode))
  }

  function isPromotedPlaceholder(node: SalesHierarchyNode): boolean {
    const name = String(node.name || "")
    const has_suffix = name.includes(" — ")
    const has_live_meta = node.metadata?.opportunity?.status === "live"
    return has_suffix && has_live_meta
  }

  function visit(node: SalesHierarchyNode): SalesHierarchyNode | null {
    const next_children = Array.isArray(node.children)
      ? node.children
          .map((child: any) => visit(child as SalesHierarchyNode))
          .filter((child: SalesHierarchyNode | null): child is SalesHierarchyNode => child !== null)
      : node.children

    const next_node: SalesHierarchyNode = {
      ...node,
      children: next_children as any,
    }

    if (isPromotedPlaceholder(next_node) && !subtreeHasData(next_node)) {
      return null
    }
    return next_node
  }

  return nodes
    .map((node: SalesHierarchyNode) => visit(node))
    .filter((node: SalesHierarchyNode | null): node is SalesHierarchyNode => node !== null)
}

export function removeOpportunity(nodes: SalesHierarchyNode[], opportunityId: string): SalesHierarchyNode[] {
  function removeNode(node: SalesHierarchyNode): SalesHierarchyNode | null {
    const opportunity = getOpportunity(node)
    if (opportunity?.id === opportunityId) {
      return null
    }

    const children = Array.isArray(node.children)
      ? node.children
          .map((child: any) => removeNode(child as SalesHierarchyNode))
          .filter((child: SalesHierarchyNode | null): child is SalesHierarchyNode => child !== null)
      : node.children

    return {
      ...(node as any),
      children: children as any,
    } as SalesHierarchyNode
  }

  return nodes
    .map((node: SalesHierarchyNode) => removeNode(node))
    .filter((node: SalesHierarchyNode | null): node is SalesHierarchyNode => node !== null)
}

export function meshOpportunitiesIntoRelatedRows(nodes: SalesHierarchyNode[]): SalesHierarchyNode[] {
  const clonedNodes = nodes.map((node: SalesHierarchyNode) => structuredClone(node))
  const baseRowByPath = new Map<string, SalesHierarchyNode>()
  const mergedOpportunityNodeIds = new Set<string>()

  function nodePath(parentPath: string, node: SalesHierarchyNode): string {
    return `${parentPath}/${node.level}:${node.name.toLowerCase().trim()}`
  }

  function indexBaseRows(node: SalesHierarchyNode, parentPath: string): void {
    const currentPath = nodePath(parentPath, node)
    if (!isOpportunityNode(node)) {
      baseRowByPath.set(currentPath, node)
    }
    if (Array.isArray(node.children)) {
      node.children.forEach((child: any) => indexBaseRows(child as SalesHierarchyNode, currentPath))
    }
  }

  /**
   * A branch merges only when its ENTIRE subtree lines up with the base tree:
   * every node has a same-path base counterpart, no counterpart belongs to a different
   * opportunity, and leaves land on leaves (a leaf merged onto an interior row is
   * invisible to the leaf-only totals). Anything less used to half-merge: the matched
   * ancestors were pruned and their unmatched children vanished with them
   * (imported branches named identically to baseline rows lost whole subtrees).
   */
  function subtreeFullyMeshable(node: SalesHierarchyNode, currentPath: string, opportunityId: string): boolean {
    const target = baseRowByPath.get(currentPath)
    if (!target) {
      return false
    }
    const targetOpportunity = getOpportunity(target)
    if (targetOpportunity && targetOpportunity.id !== opportunityId) {
      return false
    }
    const nodeChildren = Array.isArray(node.children) ? (node.children as SalesHierarchyNode[]) : []
    const targetChildren = Array.isArray(target.children) ? target.children : []
    if (nodeChildren.length === 0) {
      return targetChildren.length === 0
    }
    if (targetChildren.length === 0) {
      return false
    }
    return nodeChildren.every((child: SalesHierarchyNode) =>
      subtreeFullyMeshable(child, nodePath(currentPath, child), opportunityId)
    )
  }

  function mergeSubtreeIntoBase(node: SalesHierarchyNode, currentPath: string): void {
    const targetNode = baseRowByPath.get(currentPath)
    if (!targetNode) {
      return
    }
    mergeHierarchySalesData(targetNode, node)
    const opportunity = getOpportunity(node)
    if (opportunity) {
      targetNode.metadata = {
        ...(targetNode.metadata || {}),
        opportunity: (targetNode.metadata?.opportunity as ForecastOpportunityMetadata | undefined) || opportunity,
      }
    }
    mergedOpportunityNodeIds.add(node.id)
    if (Array.isArray(node.children)) {
      node.children.forEach((child: any) =>
        mergeSubtreeIntoBase(child as SalesHierarchyNode, nodePath(currentPath, child as SalesHierarchyNode))
      )
    }
  }

  function mergeOpportunityRows(node: SalesHierarchyNode, parentPath: string): void {
    const currentPath = nodePath(parentPath, node)
    const opportunity = getOpportunity(node)
    if (opportunity && opportunity.status !== "live") {
      if (opportunity.status === "lost") {
        // Lost branches are zeroed history — never fold them into (or brand them onto) baseline rows.
        return
      }
      if (subtreeFullyMeshable(node, currentPath, opportunity.id)) {
        mergeSubtreeIntoBase(node, currentPath)
      } else if (baseRowByPath.has(currentPath) && !isClassOfTradeLevel(String(node.level))) {
        // Name collision that cannot merge cleanly: keep the branch whole and
        // disambiguate the label, mirroring `mergeOrAppendOpportunityChild`.
        const tag = (opportunity.name && opportunity.name.trim()) || "Opportunity"
        if (!String(node.name).includes(tag)) {
          node.name = `${String(node.name)} — ${tag}`
        }
      }
      // An opportunity branch merges whole or stays whole — never walk into it.
      return
    }

    if (Array.isArray(node.children)) {
      node.children.forEach((child: any) => mergeOpportunityRows(child as SalesHierarchyNode, currentPath))
    }
  }

  function pruneMergedOpportunityRows(node: SalesHierarchyNode): SalesHierarchyNode | null {
    const opportunity = getOpportunity(node)
    if (opportunity && mergedOpportunityNodeIds.has(node.id)) {
      return null
    }

    const children = Array.isArray(node.children)
      ? node.children
          .map((child: any) => pruneMergedOpportunityRows(child as SalesHierarchyNode))
          .filter((child: SalesHierarchyNode | null): child is SalesHierarchyNode => child !== null)
      : node.children

    return {
      ...(node as any),
      children: children as any,
    } as SalesHierarchyNode
  }

  clonedNodes.forEach((node: SalesHierarchyNode) => indexBaseRows(node, "root"))
  clonedNodes.forEach((node: SalesHierarchyNode) => mergeOpportunityRows(node, "root"))

  return clonedNodes
    .map((node: SalesHierarchyNode) => pruneMergedOpportunityRows(node))
    .filter((node: SalesHierarchyNode | null): node is SalesHierarchyNode => node !== null)
}

/**
 * A row's period quantity as the dialog holds volume: cases **per store per month**. A plan that
 * counts account totals divides by the doors first, and a weekly period is scaled up to a month —
 * otherwise a row showing 1,116 cases across 1,313 Walmart doors in one week prefills as 1,116
 * cases per store per month.
 */
export function prefill_cases_per_store_per_month(
  cases_sold: number,
  store_count: number,
  period_key: string,
  basis: QuantityBasis,
): number {
  const per_store = basis === "total" ? (store_count > 0 ? cases_sold / store_count : 0) : cases_sold
  const per_month = /^\d{4}-W\d{2}$/.test(period_key) ? per_store / MONTHS_PER_WEEK : per_store
  return Math.round(per_month * 10000) / 10000
}

export function buildOpportunityPrefillFromRow(row: TableRow, basis: QuantityBasis = "per_store"): OpportunityPrefillFormData {
  const periodKeys = Object.keys(row.monthly_data).sort()
  const firstPeriodKey = periodKeys[0] || ""
  const firstPeriod = firstPeriodKey ? row.monthly_data[firstPeriodKey] : undefined
  // Cents-rounded: the raw division leaks full float precision into the dialog's
  // "Cost per case" input (e.g. 6.485446740858506).
  const raw_case_cost = row.case_cost || (
    firstPeriod && firstPeriod.cases_sold > 0
      ? firstPeriod.volume_dollars / Math.max(1, firstPeriod.cases_sold)
      : 0
  )
  const caseCost = Math.round(raw_case_cost * 100) / 100

  const productLineName = row.level === HierarchyLevel.PRODUCT_LINE || row.level === HierarchyLevel.SKU
    ? row.name
    : ""
  const customer = row.level === HierarchyLevel.CUSTOMER || row.level === HierarchyLevel.RETAILER
    ? row.name
    : ""

  return {
    classOfTrade: "",
    customer,
    routeToMarket: "",
    customerIsOther: customer.length > 0,
    planningBySku: row.level === HierarchyLevel.SKU,
    isOpportunity: true,
    opportunityName: `${row.name} opportunity`,
    opportunityLikelihood: 50,
    growthExpectations: [{
      id: `growth_${Date.now()}`,
      // Chain name is an optional sub-banner under the customer — never the source row's
      // own name (a SKU row used to land its SKU name here).
      name: "",
      initialDoors: firstPeriod?.store_count || 0,
      startMonth: firstPeriodKey,
      storeCountGrowthMultiplier: row.forecast_config?.store_count_growth_multiplier ?? 0.02,
      productLineName,
      initialCasesPerMonth: prefill_cases_per_store_per_month(
        firstPeriod?.cases_sold || 0,
        firstPeriod?.store_count || 0,
        firstPeriodKey,
        basis,
      ),
      costPerCase: caseCost,
      sameStoreSalesMultiplier: row.forecast_config?.same_store_sales_multiplier ?? 0.03,
      productSku: (row as any).product_sku || "",
      skuName: row.level === HierarchyLevel.SKU ? row.name : "",
    }],
  }
}

interface OpportunityPrefillContext {
  class_of_trade: string
  customer: string
  product_line_name: string
  product_sku: string
  sku_name: string
  product_id: string
  product_upc: string
  units_per_case: number | undefined
}

function defaultOpportunityPrefillContext(): OpportunityPrefillContext {
  return {
    class_of_trade: "",
    customer: "",
    product_line_name: "",
    product_sku: "",
    sku_name: "",
    product_id: "",
    product_upc: "",
    units_per_case: undefined,
  }
}

export function buildOpportunityPrefillFromHierarchyRow(
  row: TableRow,
  hierarchy: SalesHierarchyNode[]
): OpportunityPrefillFormData {
  const fallback = buildOpportunityPrefillFromRow(row, plan_quantity_basis(hierarchy))
  const context = findOpportunityPrefillContext(row.id, hierarchy)
  if (!context) {
    return fallback
  }

  return {
    ...fallback,
    classOfTrade: context.class_of_trade || fallback.classOfTrade,
    customer: context.customer || fallback.customer,
    growthExpectations: fallback.growthExpectations.map((growthExpectation: OpportunityPrefillGrowthExpectation) => ({
      ...growthExpectation,
      productLineName: context.product_line_name || growthExpectation.productLineName,
      productSku: context.product_sku || growthExpectation.productSku || "",
      skuName: context.sku_name || growthExpectation.skuName || "",
      ...(context.product_id ? { productId: context.product_id } : {}),
      ...(context.product_upc ? { productUpc: context.product_upc } : {}),
      ...(context.units_per_case ? { unitsPerCase: context.units_per_case } : {}),
    })),
  }
}

/** Same ancestry prefill as an opportunity, but the dialog opens in “standard sales source” mode (not an opportunity scenario). */
export function buildSalesSourcePrefillFromHierarchyRow(
  row: TableRow,
  hierarchy: SalesHierarchyNode[]
): OpportunityPrefillFormData {
  return {
    ...buildOpportunityPrefillFromHierarchyRow(row, hierarchy),
    isOpportunity: false,
  }
}

function findOpportunityPrefillContext(
  targetNodeId: string,
  nodes: SalesHierarchyNode[]
): OpportunityPrefillContext | null {
  for (const node of nodes) {
    const context = findOpportunityPrefillContextForNode(node, targetNodeId, defaultOpportunityPrefillContext())
    if (context) {
      return context
    }
  }
  return null
}

function findOpportunityPrefillContextForNode(
  node: SalesHierarchyNode,
  targetNodeId: string,
  inheritedContext: OpportunityPrefillContext
): OpportunityPrefillContext | null {
  const nextContext = {
    ...inheritedContext,
  }

  if (node.level === HierarchyLevel.CLASS_OF_TRADE) {
    nextContext.class_of_trade = node.name
  }
  {
    const level_str = String(node.level)
    if (
      level_str === HierarchyLevel.CUSTOMER ||
      level_str === HierarchyLevel.RETAILER ||
      level_str === "customer" ||
      level_str === HierarchyLevel.DISTRIBUTOR
    ) {
      nextContext.customer = node.name
    }
  }
  if (node.level === HierarchyLevel.PRODUCT_LINE) {
    nextContext.product_line_name = node.name
  }
  if (node.level === HierarchyLevel.SKU) {
    nextContext.product_sku = ((node as unknown) as { product_sku?: string }).product_sku || ""
    nextContext.sku_name = node.name
    // Catalog identity travels with the prefill: `sku:<upc>` stable keys and
    // catalog metadata mean this SKU is already actualization-resolvable.
    const stable = String((node as { stable_key?: string }).stable_key ?? "")
    nextContext.product_upc = stable.startsWith("sku:") ? stable.slice("sku:".length) : ""
    const metadata = (node.metadata ?? {}) as { catalog_product_id?: unknown; units_per_case?: unknown }
    nextContext.product_id = metadata.catalog_product_id ? String(metadata.catalog_product_id) : ""
    nextContext.units_per_case = metadata.units_per_case ? Number(metadata.units_per_case) : undefined
  }

  if (node.id === targetNodeId) {
    return nextContext
  }

  if (Array.isArray(node.children)) {
    for (const childNode of node.children) {
      const match = findOpportunityPrefillContextForNode(childNode as SalesHierarchyNode, targetNodeId, nextContext)
      if (match) {
        return match
      }
    }
  }

  return null
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("applyOpportunityScenario", () => {
    const opportunity = {
      id: "opp-1",
      name: "Retail launch",
      likelihood_pct: 25,
      status: "open" as const,
      color: "#0ea5e9",
    }
    const nodes = [{
      id: "node-1",
      name: "Retail launch",
      level: HierarchyLevel.PRODUCT_LINE,
      metadata: { opportunity },
      sales_data: [{
        period: { year: 2026, month: 1, period_key: "2026-01" },
        metrics: { store_count: 100, cases_sold: 200, volume_dollars: 1000, case_cost: 5 },
        opportunity_base_metrics: { store_count: 100, cases_sold: 200, volume_dollars: 1000, case_cost: 5 },
      }],
    }] as SalesHierarchyNode[]

    it("weights open opportunities by likelihood", () => {
      const weighted = applyOpportunityScenario(nodes, "weighted", new Set())
      expect((weighted[0] as any).sales_data[0].metrics.volume_dollars).toBe(250)
      expect((weighted[0] as any).sales_data[0].metrics.cases_sold).toBe(50)
    })

    it("can hide opportunities for the baseline view", () => {
      const baseline = applyOpportunityScenario(nodes, "baseline", new Set())
      expect((baseline[0] as any).sales_data[0].metrics.volume_dollars).toBe(0)
    })

    it("can show full upside", () => {
      const fullUpside = applyOpportunityScenario(nodes, "full_upside", new Set())
      expect((fullUpside[0] as any).sales_data[0].metrics.volume_dollars).toBe(1000)
    })
  })

  describe("collectForecastOpportunities", () => {
    it("computes weighted revenue as full upside × likelihood using opportunity_base_metrics", () => {
      const opportunity = {
        id: "opp-x",
        name: "Test",
        likelihood_pct: 40,
        status: "open" as const,
        color: "#fff",
      }
      const forest = [
        {
          id: "leaf",
          name: "Leaf",
          level: HierarchyLevel.PRODUCT_LINE,
          sales_data: [{
            period: { year: 2026, month: 1, period_key: "2026-01" },
            metrics: { store_count: 1, cases_sold: 1, volume_dollars: 9999, case_cost: 1 },
            opportunity_base_metrics: { store_count: 10, cases_sold: 10, volume_dollars: 10000, case_cost: 1 },
          }],
          metadata: { opportunity },
          children: [],
        },
      ] as SalesHierarchyNode[]
      const summaries = collectForecastOpportunities(forest)
      expect(summaries).toHaveLength(1)
      expect(summaries[0].full_revenue).toBe(10000)
      expect(summaries[0].weighted_revenue).toBe(4000)
      expect(summaries[0].likelihood_pct).toBe(40)
    })

    it("records deepest hierarchy path on leaf rows", () => {
      const opportunity = {
        id: "opp-path",
        name: "Path test",
        likelihood_pct: 100,
        status: "open" as const,
        color: "#000",
      }
      const forest = [
        {
          id: "cot",
          name: "Natural",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [
            {
              id: "cust",
              name: "Whole Foods",
              level: HierarchyLevel.CUSTOMER,
              parent_id: "cot",
              sales_data: [],
              children: [
                {
                  id: "leaf",
                  name: "Bars",
                  level: HierarchyLevel.PRODUCT_LINE,
                  parent_id: "cust",
                  sales_data: [{
                    period: { year: 2026, month: 1, period_key: "2026-01" },
                    metrics: { store_count: 1, cases_sold: 1, volume_dollars: 100, case_cost: 1 },
                    opportunity_base_metrics: { store_count: 1, cases_sold: 1, volume_dollars: 100, case_cost: 1 },
                  }],
                  metadata: { opportunity },
                  children: [],
                },
              ],
            },
          ],
        },
      ] as SalesHierarchyNode[]
      const summaries = collectForecastOpportunities(forest)
      expect(summaries[0].hierarchy_path).toContain("Natural")
      expect(summaries[0].hierarchy_path).toContain("Whole Foods")
      expect(summaries[0].hierarchy_path).toContain("Bars")
    })
  })

  describe("buildOpportunityPrefillFromHierarchyRow", () => {
    it("uses hierarchy ancestry for class of trade and customer", () => {
      const row = {
        id: "product-line-id",
        name: "Protein Bars",
        level: HierarchyLevel.PRODUCT_LINE,
        depth: 2,
        is_expanded: false,
        is_visible: true,
        has_children: false,
        monthly_data: {
          "2026-01": {
            store_count: 10,
            volume_dollars: 1000,
            cases_sold: 50,
            is_forecast: true,
            is_actual: false,
          },
        },
        totals: {
          total_stores: 10,
          total_volume: 1000,
          total_cases: 50,
          total_units: 0,
          total_promo_lift_dollars: 0,
          total_promo_lift_units: 0,
          average_stores_per_period: 10,
          average_volume_per_period: 1000,
          average_cases_per_period: 50,
        },
      } as TableRow

      const hierarchy = [{
        id: "class-id",
        name: "Natural/Specialty",
        level: HierarchyLevel.CLASS_OF_TRADE,
        sales_data: [],
        children: [{
          id: "customer-id",
          name: "Whole Foods",
          level: HierarchyLevel.CUSTOMER,
          parent_id: "class-id",
          sales_data: [],
          children: [{
            id: "product-line-id",
            name: "Protein Bars",
            level: HierarchyLevel.PRODUCT_LINE,
            parent_id: "customer-id",
            sales_data: [],
          }],
        }],
      }] as unknown as SalesHierarchyNode[]

      const prefill = buildOpportunityPrefillFromHierarchyRow(row, hierarchy)
      expect(prefill.classOfTrade).toBe("Natural/Specialty")
      expect(prefill.customer).toBe("Whole Foods")
      expect(prefill.growthExpectations[0].productLineName).toBe("Protein Bars")
      // Regression: the source row's name leaked into the optional Chain name field
      // (a SKU row put its SKU name there). Chain name must start blank.
      expect(prefill.growthExpectations[0].name).toBe("")
    })
  })

  describe("prefill_cases_per_store_per_month", () => {
    it("passes a per-store monthly row through", () => {
      expect(prefill_cases_per_store_per_month(50, 10, "2026-01", "per_store")).toBe(50)
    })

    it("divides an account total by its doors and scales a week up to a month", () => {
      // Sanzo, Walmart, Lychee 6-pack: 1,116 cases across 1,313 doors in one week is 0.85 a
      // store a week, 3.70 a store a month.
      expect(prefill_cases_per_store_per_month(1116, 1313, "2026-W45", "total")).toBeCloseTo(3.6957, 3)
      expect(prefill_cases_per_store_per_month(1116, 1313, "2026-11", "total")).toBeCloseTo(0.85, 3)
    })

    it("is zero for a total row with no doors", () => {
      expect(prefill_cases_per_store_per_month(40, 0, "2026-W45", "total")).toBe(0)
    })
  })

  describe("spliceOpportunityBranchesIntoExistingCustomers", () => {
    it("splices opportunity customer branch under existing customer with same name", () => {
      const oppMeta = {
        id: "opp-kehe",
        name: "KeHE opportunity",
        likelihood_pct: 50,
        status: "open" as const,
        color: "#0ea5e9",
      }
      const forest = [
        {
          id: "cot-existing",
          name: "Natural",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [{
            id: "kehe-base",
            name: "KeHE",
            level: HierarchyLevel.CUSTOMER,
            parent_id: "cot-existing",
            sales_data: [],
            children: [],
          }],
        },
        {
          id: "cot-opp",
          name: "Pipeline",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          metadata: { opportunity: oppMeta },
          children: [{
            id: "kehe-opp",
            name: "KeHE",
            level: HierarchyLevel.CUSTOMER,
            parent_id: "cot-opp",
            sales_data: [],
            metadata: { opportunity: oppMeta },
            children: [{
              id: "pl-sparkling",
              name: "Sparkling",
              level: HierarchyLevel.PRODUCT_LINE,
              parent_id: "kehe-opp",
              sales_data: [{
                period: { year: 2026, month: 4, period_key: "2026-04" },
                metrics: { store_count: 3, cases_sold: 3, volume_dollars: 250, case_cost: 10 },
              }],
              metadata: { opportunity: oppMeta },
            }],
          }],
        },
      ] as unknown as SalesHierarchyNode[]

      const result = spliceOpportunityBranchesIntoExistingCustomers(forest)
      expect(result).toHaveLength(1)
      const kehe = (result[0] as any).children[0]
      expect(kehe.name).toBe("KeHE")
      expect(kehe.children).toHaveLength(1)
      expect(kehe.children[0].name).toBe("Sparkling")
      expect(kehe.children[0].metadata?.opportunity?.id).toBe("opp-kehe")
    })

    it("appends opportunity product line as a sibling when a base product line with the same name exists", () => {
      const oppMeta = {
        id: "opp-kehe",
        name: "KeHE opportunity",
        likelihood_pct: 50,
        status: "open" as const,
        color: "#0ea5e9",
      }
      const forest = [
        {
          id: "cot-existing",
          name: "Natural",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [
            {
              id: "kehe-base",
              name: "KeHE",
              level: HierarchyLevel.CUSTOMER,
              parent_id: "cot-existing",
              sales_data: [],
              children: [
                {
                  id: "pl-base",
                  name: "Sparkling",
                  level: HierarchyLevel.PRODUCT_LINE,
                  parent_id: "kehe-base",
                  sales_data: [
                    {
                      period: { year: 2026, month: 4, period_key: "2026-04" },
                      metrics: { store_count: 5, cases_sold: 5, volume_dollars: 500, case_cost: 10 },
                    },
                  ],
                  children: [],
                },
              ],
            },
          ],
        },
        {
          id: "cot-opp",
          name: "Pipeline",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          metadata: { opportunity: oppMeta },
          children: [
            {
              id: "kehe-opp",
              name: "KeHE",
              level: HierarchyLevel.CUSTOMER,
              parent_id: "cot-opp",
              sales_data: [],
              metadata: { opportunity: oppMeta },
              children: [
                {
                  id: "pl-sparkling",
                  name: "Sparkling",
                  level: HierarchyLevel.PRODUCT_LINE,
                  parent_id: "kehe-opp",
                  sales_data: [
                    {
                      period: { year: 2026, month: 4, period_key: "2026-04" },
                      metrics: { store_count: 3, cases_sold: 3, volume_dollars: 250, case_cost: 10 },
                    },
                  ],
                  metadata: { opportunity: oppMeta },
                  children: [],
                },
              ],
            },
          ],
        },
      ] as unknown as SalesHierarchyNode[]

      const result = spliceOpportunityBranchesIntoExistingCustomers(forest)
      expect(result).toHaveLength(1)
      const kehe = (result[0] as any).children[0]
      expect(kehe.children).toHaveLength(2)
      const vol = (n: SalesHierarchyNode) =>
        (n.sales_data || []).reduce((s: number, d: SalesDataPoint) => s + Number(d.metrics?.volume_dollars ?? 0), 0)
      const base = kehe.children.find((c: any) => c.id === "pl-base")
      const oppRow = kehe.children.find((c: any) => c.id !== "pl-base")
      expect(base && vol(base)).toBe(500)
      expect(oppRow?.name).toContain("Sparkling")
      expect(oppRow?.name).toContain("KeHE opportunity")
    })

    it("splices into the highest-volume matching buyer when duplicates share a name", () => {
      const oppMeta = {
        id: "opp-kehe",
        name: "KeHE opportunity",
        likelihood_pct: 50,
        status: "open" as const,
        color: "#0ea5e9",
      }
      const forest = [
        {
          id: "cot-a",
          name: "Natural",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [{
            id: "kehe-low",
            name: "KeHE",
            level: HierarchyLevel.CUSTOMER,
            parent_id: "cot-a",
            sales_data: [{
              period: { year: 2026, month: 4, period_key: "2026-04" },
              metrics: { store_count: 1, cases_sold: 1, volume_dollars: 100, case_cost: 10 },
            }],
            children: [],
          }],
        },
        {
          id: "cot-b",
          name: "Club",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [{
            id: "kehe-high",
            name: "KeHE",
            level: HierarchyLevel.CUSTOMER,
            parent_id: "cot-b",
            sales_data: [{
              period: { year: 2026, month: 4, period_key: "2026-04" },
              metrics: { store_count: 10, cases_sold: 10, volume_dollars: 9000, case_cost: 10 },
            }],
            children: [],
          }],
        },
        {
          id: "cot-opp",
          name: "Pipeline",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          metadata: { opportunity: oppMeta },
          children: [{
            id: "kehe-opp",
            name: "KeHE",
            level: HierarchyLevel.CUSTOMER,
            parent_id: "cot-opp",
            sales_data: [],
            metadata: { opportunity: oppMeta },
            children: [{
              id: "pl-sparkling",
              name: "Sparkling",
              level: HierarchyLevel.PRODUCT_LINE,
              parent_id: "kehe-opp",
              sales_data: [{
                period: { year: 2026, month: 4, period_key: "2026-04" },
                metrics: { store_count: 3, cases_sold: 3, volume_dollars: 250, case_cost: 10 },
              }],
              metadata: { opportunity: oppMeta },
            }],
          }],
        },
      ] as unknown as SalesHierarchyNode[]

      const result = spliceOpportunityBranchesIntoExistingCustomers(forest)
      expect(result).toHaveLength(2)
      const high = result.flatMap((r: SalesHierarchyNode) => r.children || []).find((c: any) => c.id === "kehe-high") as SalesHierarchyNode
      expect(high.children).toHaveLength(1)
      expect(high.children![0].name).toBe("Sparkling")
    })

    it("finds the opportunity buyer when the Pipeline root has multiple children", () => {
      const oppMeta = {
        id: "opp-kehe",
        name: "KeHE opportunity",
        likelihood_pct: 50,
        status: "open" as const,
        color: "#0ea5e9",
      }
      const forest = [
        {
          id: "cot-existing",
          name: "Natural",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [{
            id: "kehe-base",
            name: "KeHE",
            level: HierarchyLevel.CUSTOMER,
            parent_id: "cot-existing",
            sales_data: [],
            children: [],
          }],
        },
        {
          id: "cot-opp",
          name: "Pipeline",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          metadata: { opportunity: oppMeta },
          children: [
            {
              id: "noise",
              name: "Other buyer",
              level: HierarchyLevel.CUSTOMER,
              parent_id: "cot-opp",
              sales_data: [],
              children: [],
            },
            {
              id: "kehe-opp",
              name: "KeHE",
              level: HierarchyLevel.CUSTOMER,
              parent_id: "cot-opp",
              sales_data: [],
              metadata: { opportunity: oppMeta },
              children: [{
                id: "pl-sparkling",
                name: "Sparkling",
                level: HierarchyLevel.PRODUCT_LINE,
                parent_id: "kehe-opp",
                sales_data: [{
                  period: { year: 2026, month: 4, period_key: "2026-04" },
                  metrics: { store_count: 3, cases_sold: 3, volume_dollars: 250, case_cost: 10 },
                }],
                metadata: { opportunity: oppMeta },
              }],
            },
          ],
        },
      ] as unknown as SalesHierarchyNode[]

      const result = spliceOpportunityBranchesIntoExistingCustomers(forest)
      expect(result).toHaveLength(2)
      const kehe = (result.find((r: SalesHierarchyNode) => r.id === "cot-existing") as any).children[0]
      expect(kehe.children).toHaveLength(1)
      expect(kehe.children[0].name).toBe("Sparkling")
    })

    it("splices a floating opportunity distributor root into an existing matching buyer", () => {
      const oppMeta = {
        id: "opp-kehe",
        name: "KeHE opportunity",
        likelihood_pct: 50,
        status: "open" as const,
        color: "#0ea5e9",
      }
      const forest = [
        {
          id: "cot-existing",
          name: "Natural",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [{
            id: "kehe-base",
            name: "KeHE",
            level: HierarchyLevel.DISTRIBUTOR,
            parent_id: "cot-existing",
            sales_data: [],
            children: [],
          }],
        },
        {
          id: "kehe-opp-root",
          name: "KeHE",
          level: HierarchyLevel.DISTRIBUTOR,
          sales_data: [],
          metadata: { opportunity: oppMeta },
          children: [{
            id: "pl-sparkling",
            name: "Sparkling",
            level: HierarchyLevel.PRODUCT_LINE,
            parent_id: "kehe-opp-root",
            sales_data: [{
              period: { year: 2026, month: 4, period_key: "2026-04" },
              metrics: { store_count: 3, cases_sold: 3, volume_dollars: 250, case_cost: 10 },
            }],
            metadata: { opportunity: oppMeta },
          }],
        },
      ] as unknown as SalesHierarchyNode[]

      const result = spliceOpportunityBranchesIntoExistingCustomers(forest)
      expect(result).toHaveLength(1)
      const kehe = (result[0] as any).children[0]
      expect(kehe.children).toHaveLength(1)
      expect(kehe.children[0].name).toBe("Sparkling")
    })
  })

  describe("collectExpandedAncestorIdsForOpportunityId", () => {
    it("collects ancestor ids toward a matching open opportunity row", () => {
      const oppMeta = {
        id: "opp-target",
        name: "X",
        likelihood_pct: 50,
        status: "open" as const,
        color: "#fff",
      }
      const roots = [{
        id: "a",
        name: "A",
        level: HierarchyLevel.CLASS_OF_TRADE,
        sales_data: [],
        children: [{
          id: "b",
          name: "B",
          level: HierarchyLevel.CUSTOMER,
          sales_data: [],
          children: [{
            id: "c",
            name: "C",
            level: HierarchyLevel.PRODUCT_LINE,
            sales_data: [],
            metadata: { opportunity: oppMeta },
          }],
        }],
      }] as unknown as SalesHierarchyNode[]

      const ids = collectExpandedAncestorIdsForOpportunityId(roots, "opp-target")
      expect(ids.has("a")).toBe(true)
      expect(ids.has("b")).toBe(true)
      expect(ids.has("c")).toBe(true)
    })
  })

  describe("collectExpandedAncestorIdsForAllOpenOpportunities", () => {
    it("collects paths for every open opportunity node", () => {
      const oppMeta = {
        id: "o1",
        name: "O1",
        likelihood_pct: 50,
        status: "open" as const,
        color: "#fff",
      }
      const roots = [{
        id: "root",
        name: "R",
        level: HierarchyLevel.CLASS_OF_TRADE,
        sales_data: [],
        children: [{
          id: "leaf",
          name: "L",
          level: HierarchyLevel.PRODUCT_LINE,
          sales_data: [],
          metadata: { opportunity: oppMeta },
        }],
      }] as unknown as SalesHierarchyNode[]

      const ids = collectExpandedAncestorIdsForAllOpenOpportunities(roots)
      expect(ids.has("root")).toBe(true)
      expect(ids.has("leaf")).toBe(true)
    })
  })

  describe("removeOpportunity", () => {
    it("removes nodes tied to the selected opportunity id", () => {
      const nodes = [{
        id: "root",
        name: "Root",
        level: HierarchyLevel.CLASS_OF_TRADE,
        sales_data: [],
        children: [{
          id: "opp-node",
          name: "Opportunity row",
          level: HierarchyLevel.PRODUCT_LINE,
          sales_data: [],
          metadata: {
            opportunity: {
              id: "opp-1",
              name: "Opportunity",
              likelihood_pct: 50,
              status: "open",
              color: "#0ea5e9",
            },
          },
        }],
      }] as unknown as SalesHierarchyNode[]

      const updated = removeOpportunity(nodes, "opp-1")
      expect((updated[0] as any).children).toHaveLength(0)
    })
  })

  describe("cleanupPromotedOpportunityPlaceholders", () => {
    it("removes live placeholder rows with no data", () => {
      const oppMeta = {
        id: "opp-1",
        name: "UNFI West opportunity",
        likelihood_pct: 100,
        status: "live" as const,
        color: "#0ea5e9",
      }
      const forest = [
        {
          id: "root",
          name: "Root",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [
            {
              id: "placeholder",
              name: "Sparkling Water - SS — UNFI West opportunity",
              level: HierarchyLevel.PRODUCT_LINE,
              metadata: { opportunity: oppMeta },
              sales_data: [],
              children: [],
            },
          ],
        },
      ] as unknown as SalesHierarchyNode[]

      const cleaned = cleanupPromotedOpportunityPlaceholders(forest)
      expect((cleaned[0] as any).children).toHaveLength(0)
    })
  })

  describe("opportunity lifecycle (won / lost / reopen)", () => {
    function opp_branch() {
      return [{
        id: "cot",
        name: "Natural/Specialty",
        level: HierarchyLevel.CLASS_OF_TRADE,
        sales_data: [],
        children: [{
          id: "opp-customer",
          name: "Kroger",
          level: HierarchyLevel.CUSTOMER as HierarchyLevel,
          metadata: { opportunity: { id: "opp-1", name: "Expansion", likelihood_pct: 40, status: "open", color: "#0ea5e9" } },
          sales_data: [{
            period: { year: 2026, month: 1, period_key: "2026-01" },
            metrics: { store_count: 4, cases_sold: 40, volume_dollars: 400, case_cost: 10 },
            source_kind: "opportunity",
            opportunity_base_metrics: { store_count: 10, cases_sold: 100, volume_dollars: 1000, case_cost: 10 },
          }],
        }],
      }] as unknown as SalesHierarchyNode[]
    }

    it("marking lost keeps the rows and the record but zeroes the contribution", () => {
      const next = markOpportunityLost(opp_branch(), "opp-1")
      const node = (next[0] as any).children[0]
      expect(node.metadata.opportunity.status).toBe("lost")
      // The rows survive (win-rate history), their contribution does not.
      expect(node.sales_data[0].metrics.volume_dollars).toBe(0)
      expect(node.sales_data[0].opportunity_base_metrics.volume_dollars).toBe(1000)
      // Lost deals leave the open panel and every scenario total.
      expect(collectForecastOpportunities(next)).toHaveLength(0)
      const weighted = applyOpportunityScenario(next, "full_upside", new Set())
      expect((weighted[0] as any).children[0].sales_data[0].metrics.volume_dollars).toBe(0)
    })

    it("promoting keeps the base-metrics record and the pre-win likelihood", () => {
      const next = promoteOpportunityToLive(opp_branch(), "opp-1")
      const node = (next[0] as any).children[0]
      expect(node.metadata.opportunity.status).toBe("live")
      expect(node.metadata.opportunity.likelihood_pct_at_close).toBe(40)
      expect(node.sales_data[0].metrics.volume_dollars).toBe(1000)
      // The unscaled record survives the win — promotion is auditable and reversible.
      expect(node.sales_data[0].opportunity_base_metrics.volume_dollars).toBe(1000)
    })

    it("reopening a won or lost deal restores the weighted open state", () => {
      for (const close of [promoteOpportunityToLive, markOpportunityLost]) {
        const closed = close(opp_branch(), "opp-1")
        const reopened = reopenOpportunity(closed, "opp-1")
        const node = (reopened[0] as any).children[0]
        expect(node.metadata.opportunity.status).toBe("open")
        expect(node.metadata.opportunity.likelihood_pct).toBe(40)
        expect(node.sales_data[0].metrics.volume_dollars).toBe(400)
      }
    })

    it("closed opportunities are listed separately with their outcome", () => {
      const closed = markOpportunityLost(promoteOpportunityToLive(opp_branch(), "opp-1"), "none")
      const rows = collectClosedForecastOpportunities(closed)
      expect(rows).toHaveLength(1)
      expect(rows[0].status).toBe("live")
      expect(rows[0].full_revenue).toBe(1000)
    })
  })

  describe("meshOpportunitiesIntoRelatedRows", () => {
    it("adds opportunity metrics into matching base rows and hides merged opportunity rows", () => {
      const nodes = [{
        id: "class-root",
        name: "Natural/Specialty",
        level: HierarchyLevel.CLASS_OF_TRADE,
        sales_data: [],
        children: [{
          id: "base-customer",
          name: "Whole Foods",
          level: HierarchyLevel.CUSTOMER as HierarchyLevel,
          sales_data: [],
          children: [{
            id: "base-row",
            name: "Protein Bars",
            level: HierarchyLevel.PRODUCT_LINE,
            sales_data: [{
              period: { year: 2026, month: 1, period_key: "2026-01" },
              metrics: { store_count: 10, cases_sold: 40, volume_dollars: 1000, case_cost: 25 },
            }],
          }, {
            id: "opp-row",
            name: "Protein Bars",
            level: HierarchyLevel.PRODUCT_LINE,
            sales_data: [{
              period: { year: 2026, month: 1, period_key: "2026-01" },
              metrics: { store_count: 2, cases_sold: 8, volume_dollars: 200, case_cost: 25 },
            }],
            metadata: {
              opportunity: {
                id: "opp-1",
                name: "WF Expansion",
                likelihood_pct: 50,
                status: "open",
                color: "#0ea5e9",
              },
            },
          }],
        }],
      }] as unknown as SalesHierarchyNode[]

      const meshed = meshOpportunitiesIntoRelatedRows(nodes)
      const customerChildren = (((meshed[0] as any).children || [])[0] as any).children || []
      expect(customerChildren).toHaveLength(1)
      expect(customerChildren[0].sales_data[0].metrics.volume_dollars).toBe(1200)
      expect(customerChildren[0].sales_data[0].metrics.cases_sold).toBe(48)
    })

    function opp_meta(id: string, name: string) {
      return { id, name, likelihood_pct: 50, status: "open", color: "#0ea5e9" }
    }

    function dp(volume_dollars: number) {
      return {
        period: { year: 2026, month: 1, period_key: "2026-01" },
        metrics: { store_count: 2, cases_sold: 8, volume_dollars, case_cost: 25 },
      }
    }

    it("forks an identically-named opportunity branch instead of swallowing its children", () => {
      // Regression (imported Confido branches, chip task_5240db22): an opportunity
      // product line named exactly like the baseline row was merged by path and its
      // SKU children — which have no baseline counterpart — were pruned wholesale.
      const nodes = [{
        id: "class-root",
        name: "Natural/Specialty",
        level: HierarchyLevel.CLASS_OF_TRADE,
        sales_data: [],
        children: [{
          id: "base-customer",
          name: "Kroger",
          level: HierarchyLevel.CUSTOMER as HierarchyLevel,
          sales_data: [],
          children: [
            { id: "base-pl", name: "Chips", level: HierarchyLevel.PRODUCT_LINE, sales_data: [dp(1000)] },
            {
              id: "opp-pl",
              name: "Chips",
              level: HierarchyLevel.PRODUCT_LINE,
              sales_data: [dp(200)],
              metadata: { opportunity: opp_meta("opp-1", "4 new SKUs") },
              children: [{
                id: "opp-sku",
                name: "12oz",
                level: HierarchyLevel.SKU,
                sales_data: [dp(200)],
                metadata: { opportunity: opp_meta("opp-1", "4 new SKUs") },
              }],
            },
          ],
        }],
      }] as unknown as SalesHierarchyNode[]

      const meshed = meshOpportunitiesIntoRelatedRows(nodes)
      const customerChildren = (((meshed[0] as any).children || [])[0] as any).children || []
      expect(customerChildren).toHaveLength(2)
      const base = customerChildren.find((c: any) => c.id === "base-pl")
      const forked = customerChildren.find((c: any) => c.id === "opp-pl")
      // The baseline row keeps its own numbers and does not become an opportunity.
      expect(base.sales_data[0].metrics.volume_dollars).toBe(1000)
      expect(base.metadata?.opportunity).toBeUndefined()
      // The opportunity branch survives whole, renamed so both rows are tellable apart.
      expect(forked.name).toBe("Chips — 4 new SKUs")
      expect(forked.children).toHaveLength(1)
      expect(forked.children[0].sales_data[0].metrics.volume_dollars).toBe(200)
    })

    it("does not merge a leaf opportunity onto an interior base row (leaf totals would drop it)", () => {
      const nodes = [{
        id: "class-root",
        name: "Natural/Specialty",
        level: HierarchyLevel.CLASS_OF_TRADE,
        sales_data: [],
        children: [{
          id: "base-customer",
          name: "Kroger",
          level: HierarchyLevel.CUSTOMER as HierarchyLevel,
          sales_data: [],
          children: [
            {
              id: "base-pl",
              name: "Chips",
              level: HierarchyLevel.PRODUCT_LINE,
              sales_data: [dp(1000)],
              children: [{ id: "base-sku", name: "12oz", level: HierarchyLevel.SKU, sales_data: [dp(1000)] }],
            },
            {
              id: "opp-pl",
              name: "Chips",
              level: HierarchyLevel.PRODUCT_LINE,
              sales_data: [dp(200)],
              metadata: { opportunity: opp_meta("opp-1", "Expansion") },
            },
          ],
        }],
      }] as unknown as SalesHierarchyNode[]

      const meshed = meshOpportunitiesIntoRelatedRows(nodes)
      const customerChildren = (((meshed[0] as any).children || [])[0] as any).children || []
      expect(customerChildren).toHaveLength(2)
      const forked = customerChildren.find((c: any) => c.id === "opp-pl")
      // Kept as its own leaf row: merged onto the interior base row, the $200 would
      // vanish from every leaf-only total.
      expect(forked.name).toBe("Chips — Expansion")
      expect(forked.sales_data[0].metrics.volume_dollars).toBe(200)
    })

    it("keeps a base row that already carries a different opportunity separate", () => {
      const nodes = [{
        id: "class-root",
        name: "Natural/Specialty",
        level: HierarchyLevel.CLASS_OF_TRADE,
        sales_data: [],
        children: [{
          id: "base-customer",
          name: "Kroger",
          level: HierarchyLevel.CUSTOMER as HierarchyLevel,
          sales_data: [],
          children: [
            {
              id: "other-opp-pl",
              name: "Chips",
              level: HierarchyLevel.PRODUCT_LINE,
              sales_data: [dp(300)],
              metadata: { opportunity: opp_meta("opp-2", "Other deal") },
            },
            {
              id: "opp-pl",
              name: "Chips",
              level: HierarchyLevel.PRODUCT_LINE,
              sales_data: [dp(200)],
              metadata: { opportunity: opp_meta("opp-1", "Expansion") },
            },
          ],
        }],
      }] as unknown as SalesHierarchyNode[]

      const meshed = meshOpportunitiesIntoRelatedRows(nodes)
      const customerChildren = (((meshed[0] as any).children || [])[0] as any).children || []
      // Neither opportunity is a baseline row, so nothing merges — both survive.
      expect(customerChildren).toHaveLength(2)
      const volumes = customerChildren.map((c: any) => c.sales_data[0].metrics.volume_dollars).sort()
      expect(volumes).toEqual([200, 300])
    })

    it("meshes a multi-level branch when every level matches the base shape", () => {
      function branch(idPrefix: string, opportunity?: any) {
        return {
          id: `${idPrefix}-pl`,
          name: "Chips",
          level: HierarchyLevel.PRODUCT_LINE,
          sales_data: [dp(idPrefix === "base" ? 1000 : 200)],
          ...(opportunity ? { metadata: { opportunity } } : {}),
          children: [{
            id: `${idPrefix}-sku`,
            name: "12oz",
            level: HierarchyLevel.SKU,
            sales_data: [dp(idPrefix === "base" ? 1000 : 200)],
            ...(opportunity ? { metadata: { opportunity } } : {}),
          }],
        }
      }
      const nodes = [{
        id: "class-root",
        name: "Natural/Specialty",
        level: HierarchyLevel.CLASS_OF_TRADE,
        sales_data: [],
        children: [{
          id: "base-customer",
          name: "Kroger",
          level: HierarchyLevel.CUSTOMER as HierarchyLevel,
          sales_data: [],
          children: [branch("base"), branch("opp", opp_meta("opp-1", "Expansion"))],
        }],
      }] as unknown as SalesHierarchyNode[]

      const meshed = meshOpportunitiesIntoRelatedRows(nodes)
      const customerChildren = (((meshed[0] as any).children || [])[0] as any).children || []
      expect(customerChildren).toHaveLength(1)
      const merged = customerChildren[0]
      expect(merged.sales_data[0].metrics.volume_dollars).toBe(1200)
      expect(merged.children).toHaveLength(1)
      expect(merged.children[0].sales_data[0].metrics.volume_dollars).toBe(1200)
      expect(merged.metadata?.opportunity?.id).toBe("opp-1")
    })
  })

  describe("pruneOpenOpportunityBranches", () => {
    function customer(name: string, children: any[]): any {
      return { id: name, name, level: "customer", children, sales_data: [] }
    }
    function branch(id: string, status: string): any {
      return {
        id,
        name: `${id} — deal`,
        level: "product_line",
        metadata: { opportunity: { id, name: id, likelihood_pct: 50, status, color: "#000" } },
        children: [],
        sales_data: [],
      }
    }
    function baselineLine(id: string): any {
      return { id, name: id, level: "product_line", children: [], sales_data: [] }
    }

    it("drops open branches but keeps live ones and baseline lines", () => {
      const pruned = pruneOpenOpportunityBranches([
        customer("Target", [baselineLine("MP"), branch("opp-1", "open"), branch("opp-2", "live")]),
      ])
      expect(pruned[0]?.children?.map((child: any) => child.id)).toEqual(["MP", "opp-2"])
    })

    it("removes a customer whose only children were open deals", () => {
      // An opportunity-only customer would otherwise survive as a childless node whose
      // load-time aggregate still counts as a leaf (the $347k Costco-regions bug).
      const pruned = pruneOpenOpportunityBranches([
        customer("Costco Midwest", [branch("opp-3", "open")]),
        customer("Sprouts", [baselineLine("MP")]),
      ])
      expect(pruned.map((node: any) => node.name)).toEqual(["Sprouts"])
    })
  })
}
